import styled from 'styled-components';
import { theme } from '../../../../../../../utils/theme';
import QuoteIcon from '../QuoteIcon/QuoteIcon';
import Rating from '../Rating/Rating';
import { getReviewImage } from '../../../assets/reviewImages';

export type Testimonial = {
  quote: string;
  rating?: number;
  name: string;
  age?: number;
  country?: string;
  role?: string;
  avatarSrc?: string;
  avatarAlt?: string;
};

export type TestimonialCardProps = {
  data: Testimonial;
  className?: string;
};

const TestimonialCard = ({ data, className }: TestimonialCardProps) => {
  const { quote, rating, name, age, country, role, avatarSrc, avatarAlt } =
    data;

  const formatMetadata = () => {
    const parts = [];
    if (age) parts.push(`${age} years old`);
    if (country) parts.push(country);
    return parts.join(', ');
  };

  const avatarAltText = avatarAlt || `${name} avatar`;
  const avatarImageSrc = avatarSrc ? getReviewImage(avatarSrc) : undefined;

  return (
    <CardContainer
      className={className}
      role='group'
      aria-label={`Testimonio de ${name}`}
    >
      <CardHeader>
        <QuoteIcon />
        {rating && <Rating rating={rating} />}
      </CardHeader>

      <QuoteText>{quote}</QuoteText>

      <AuthorSection>
        <Avatar>
          {avatarImageSrc ? (
            <AvatarImage src={avatarImageSrc} alt={avatarAltText} />
          ) : (
            <AvatarFallback aria-label={avatarAltText}>
              {name.charAt(0).toUpperCase()}
            </AvatarFallback>
          )}
        </Avatar>

        <AuthorInfo>
          <AuthorName>{name}</AuthorName>
          {(formatMetadata() || role) && (
            <AuthorMeta>
              {[formatMetadata(), role].filter(Boolean).join(' - ')}
            </AuthorMeta>
          )}
        </AuthorInfo>
      </AuthorSection>
    </CardContainer>
  );
};

export default TestimonialCard;

const CardContainer = styled.div`
  background: #fff;
  /* border: 1px solid ${theme.landing.colors.primary}33; */
  border-radius: 24px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 100%;
  max-width: 353px;
  /* box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05); */
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  min-height: 30px;
`;

const QuoteText = styled.p`
  font-family: 'Open Sans', sans-serif !important;
  font-size: 13px;
  line-height: 18px;
  font-style: italic;
  color: ${theme.landing.colors.primary};
  margin: 0;
  flex-grow: 1;
  @media (min-width: 600px) {
    font-size: 16px;
    line-height: 22px;
  }
  @media (min-width: 1024px) {
    font-size: 20px;
    line-height: 28px;
  }
`;

const AuthorSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: auto;
  border-top: 1px solid #283288;
  padding-top: 12px;
`;

const Avatar = styled.div`
  width: 33px;
  height: 33px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: ${theme.landing.colors.lightBlue}44;
  display: flex;
  align-items: center;
  justify-content: center;
  @media (min-width: 600px) {
    width: 56px;
    height: 56px;
  }
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AvatarFallback = styled.div`
  font-family: 'League Spartan', sans-serif !important;
  font-size: 20px;
  font-weight: 600;
  color: #283288 !important;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AuthorInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex-grow: 1;
`;

const AuthorName = styled.h3`
  font-family: 'League Spartan', sans-serif !important;
  font-size: 15px;
  line-height: 18px;
  font-weight: 700;
  color: #283288 !important;
  text-align: left;
  margin: 0;
  @media (min-width: 600px) {
    font-size: 18px;
    line-height: 30px;
  }
`;

const AuthorMeta = styled.p`
  font-family: 'League Spartan', sans-serif !important;
  font-size: 14px;
  line-height: 22px;
  font-weight: 400;
  color: #283288 !important;
  margin: 0;
  @media (min-width: 600px) {
    font-size: 18px;
    line-height: 22px;
  }
`;
